/**
 * api.js — Couche Supabase remplaçant le backend Node.js
 * Expose la même interface que l'ancien client axios : api.get/post/put/delete
 */
import { supabase } from '../supabaseClient';

// URL du backend Express (pour les opérations admin nécessitant la clé service)
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper : appeler le backend Express avec le token de session Supabase
async function callBackend(method, path, body = null) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw err('Non authentifié');
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${BACKEND_URL}/api${path}`, opts);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw err(json.message || `Erreur serveur (${res.status})`);
  return { data: json };
}

// Helper : wrapper avec timeout sur n'importe quelle Promise Supabase
function withTimeout(promise, ms = 8000, label = 'requête') {
  const t = new Promise((_, reject) =>
    setTimeout(() => reject(new Error(`Timeout ${label} (${ms}ms)`)), ms)
  );
  return Promise.race([promise, t]);
}

// Helper : exécuter un ensemble de requêtes Supabase avec fallback individuel
// Retourne un tableau de résultats (null si timeout/erreur individuelle)
async function safeAll(queries, timeoutMs = 8000) {
  return Promise.all(
    queries.map(q =>
      withTimeout(q, timeoutMs).catch(() => ({ data: null, error: null }))
    )
  );
}

// ── Helpers ──────────────────────────────────────────────────
const uid = () => crypto.randomUUID();
const today = () => new Date().toISOString().split('T')[0];
const now   = () => new Date().toISOString();

// Colonnes autorisées pour la table personnel (évite les erreurs "column not found")
const PERSONNEL_ALLOWED_FIELDS = new Set([
  'first_name', 'last_name', 'maiden_name', 'birth_date', 'birth_place',
  'address', 'phone', 'email', 'family_situation', 'emergency_contact',
  'cin_numero', 'cin_date', 'cin_lieu', 'rib', 'banque',
  'poste', 'service', 'contract_type', 'entry_date', 'salary',
  'cnaps', 'aro', 'havana', 'photo', 'status',
  'doc_diplome', 'doc_cv', 'doc_contrat',
]);

const DATE_FIELDS = new Set(['birth_date', 'cin_date', 'entry_date', 'start_date', 'end_date', 'assigned_date', 'registration_date', 'date_taken']);

// Convertit les chaînes vides en null pour les champs date
function nullifyDates(obj, dateFields = DATE_FIELDS) {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, dateFields.has(k) ? (v || null) : v])
  );
}

function pickPersonnel(body) {
  return nullifyDates(
    Object.fromEntries(Object.entries(body).filter(([k]) => PERSONNEL_ALLOWED_FIELDS.has(k)))
  );
}

function parseUrl(url) {
  const [path, qs] = url.split('?');
  const params = new URLSearchParams(qs || '');
  const segs   = path.replace(/^\//, '').split('/');
  return { path, params, segs };
}

function err(msg, code) {
  const e = new Error(msg);
  e.response = { data: { message: msg, code } };
  return e;
}

// ── Course structure BCC ─────────────────────────────────────
const COURSE_STRUCTURE = {
  English: {
    annees: [
      {
        id: 1, label: 'First Year', lessonLabel: 'Lesson',
        lessons: [
          { num:1, max:20 }, { num:2, max:25 }, { num:3, max:10 },
          { num:4, max:25 }, { num:5, max:30 }, { num:6, max:20 },
          { num:7, max:13 }, { num:8, max:10 }, { num:9, max:10 }, { num:10, max:10 },
        ],
      },
      {
        id: 2, label: 'Second Year', lessonLabel: 'Lesson',
        lessons: [
          { num:1, max:20 }, { num:2, max:25 }, { num:3, max:10 },
          { num:4, max:25 }, { num:5, max:30 }, { num:6, max:10 },
          { num:7, max:10 }, { num:8, max:10 }, { num:9, max:20 }, { num:10, max:20 },
        ],
      },
    ],
  },
  Français: {
    annees: [
      {
        id: 1, label: 'Première Année', lessonLabel: 'Leçon',
        lessons: [
          { num:1, max:18 }, { num:2, max:25 }, { num:3, max:10 },
          { num:4, max:25 }, { num:5, max:30 }, { num:6, max:20 },
          { num:7, max:13 }, { num:8, max:10 }, { num:9, max:10 }, { num:10, max:10 },
        ],
      },
    ],
  },
  Malagasy: {
    annees: [
      {
        id: 1, label: 'Ambaratonga Voalohany', lessonLabel: 'Lesona',
        lessons: [{ num:1, max:30 }, { num:2, max:30 }],
      },
      {
        id: 2, label: 'Ambaratonga Faharoa', lessonLabel: 'Lesona',
        lessons: [{ num:1, max:30 }, { num:2, max:30 }],
      },
    ],
  },
};

const getMentionBcc = pct => {
  if (pct === null || pct === undefined) return null;
  pct = parseFloat(pct);
  if (pct >= 80) return 'Très Bien';
  if (pct >= 70) return 'Bien';
  if (pct >= 60) return 'Assez Bien';
  if (pct >= 50) return 'Passable';
  return 'Insuffisant';
};

// Calcule la progression d'un étudiant à partir de ses notes Supabase
function calcProgression(student, lessons) {
  const stuNotes = lessons.filter(l => l.student_id === student.id);
  const struct = COURSE_STRUCTURE[student.language] || COURSE_STRUCTURE['Français'];

  const annees = struct.annees.map((anneeConf, idx) => {
    const anneeNotes  = stuNotes.filter(n => n.class_number === anneeConf.id);
    const notesSaisies = anneeNotes.map(n => ({ lecon: n.lesson_number, note: n.score ?? 0 }));

    const totalObtenu   = notesSaisies.reduce((s, n) => s + parseFloat(n.note || 0), 0);
    const totalPossible = anneeConf.lessons.reduce((s, l) => s + l.max, 0);
    const pourcentage   = totalPossible > 0 && notesSaisies.length > 0
      ? Math.round((totalObtenu / totalPossible) * 10000) / 100
      : null;
    const complete = notesSaisies.length >= anneeConf.lessons.length;
    const valide   = complete && pourcentage !== null && pourcentage >= 50;
    const statut   = valide ? 'valide' : complete ? 'non_valide' : 'en_cours';

    return {
      id:           anneeConf.id,
      label:        anneeConf.label,
      lessonLabel:  anneeConf.lessonLabel,
      lessons:      anneeConf.lessons,
      accessible:   idx === 0,
      notesSaisies,
      stats: {
        lecons:       notesSaisies.length,
        totalObtenu:  Math.round(totalObtenu * 100) / 100,
        totalPossible,
        total:        Math.round(totalObtenu * 100) / 100,
        pourcentage,
        moyenne:      pourcentage,
        mention:      getMentionBcc(pourcentage),
        complete,
        valide,
        statut,
      },
    };
  });

  // Débloquer les années successivement
  for (let i = 1; i < annees.length; i++) {
    annees[i].accessible = annees[i - 1].stats.valide;
  }

  const nbValidees    = annees.filter(a => a.stats.valide).length;
  const classeActuelle = Math.min(nbValidees + 1, struct.annees.length);

  // Moyenne globale : total obtenu / total possible des leçons saisies
  let globalObtenu = 0, globalPossible = 0;
  annees.forEach(a => {
    a.notesSaisies.forEach(n => {
      const lesson = a.lessons.find(l => l.num === n.lecon);
      if (lesson) { globalObtenu += parseFloat(n.note || 0); globalPossible += lesson.max; }
    });
  });
  const moyenneGlobale = globalPossible > 0
    ? Math.round((globalObtenu / globalPossible) * 10000) / 100
    : null;

  return { annees, moyenneGlobale, nbValidees, classeActuelle, totalObtenu: globalObtenu, totalPossible: globalPossible };
}

// Transforme un enregistrement bcc_students en objet attendu par BCCPage
function toEtudiant(s, lessons) {
  const prog = calcProgression(s, lessons);
  const struct = COURSE_STRUCTURE[s.language] || COURSE_STRUCTURE['Français'];
  return {
    id:               s.id,
    numero_etudiant:  s.student_number,
    prenom:           s.first_name,
    nom:              s.last_name,
    langue:           s.language,
    statut:           s.status || 'active',
    date_inscription: s.registration_date || s.created_at,
    birth_date:       s.birth_date,
    birth_place:      s.birth_place,
    address:          s.address,
    phone:            s.phone,
    email:            s.email,
    family_situation: s.family_situation,
    education_level:  s.education_level,
    occupation:       s.occupation,
    motivation:       s.motivation,
    classe_actuelle:     prog.classeActuelle,
    moyenne_globale:     prog.moyenneGlobale,
    mention_globale:     getMentionBcc(prog.moyenneGlobale),
    total_obtenu:        prog.totalObtenu,
    total_possible:      prog.totalPossible,
    nb_annees_validees:  prog.nbValidees,
    nb_annees_total:     struct.annees.length,
    progression:         { annees: prog.annees },
    course_structure:    struct,
  };
}

// ═══════════════════════════════════════════════════════════════
// GET
// ═══════════════════════════════════════════════════════════════
async function get(url) {
  const { segs, params } = parseUrl(url);

  // ── DASHBOARD ─────────────────────────────────────────────
  if (segs[0] === 'dashboard') {
    // Résultat vide par défaut — jamais null, jamais un throw
    const empty = { stats: { totalPersonnel:0, totalVolunteers:0, activeProjects:0, delayedProjects:0, criticalStock:0, pendingLeave:0 }, alerts: [], recentActivity: [] };

    try {
      const results = await safeAll([
        supabase.from('personnel').select('id, status'),
        supabase.from('volontaires').select('id, status'),
        supabase.from('projets').select('id, status, end_date'),
        supabase.from('stock_articles').select('id, current_stock, min_stock'),
        supabase.from('absences').select('id, status'),
        supabase.from('audit_logs').select('id, timestamp, user_id, action, new_value')
          .eq('action', 'LOGIN')
          .order('timestamp', { ascending: false })
          .limit(20),
      ]);

      const pers      = results[0]?.data || [];
      const vols      = results[1]?.data || [];
      const projs     = results[2]?.data || [];
      const stock     = results[3]?.data || [];
      const abs       = results[4]?.data || [];
      const loginLogs = results[5]?.data || [];

      const totalPersonnel  = pers.filter(p => p.status === 'active').length;
      const totalVolunteers = vols.filter(v => !['cancelled','rejected'].includes(v.status)).length;
      const activeProjects  = projs.filter(p => ['active','in_progress'].includes(p.status)).length;
      const delayedProjects = projs.filter(p => p.end_date && p.end_date < today() && p.status !== 'completed').length;
      const criticalStock   = stock.filter(s => (s.current_stock || 0) <= (s.min_stock || 0)).length;
      const pendingLeave    = abs.filter(a => a.status === 'pending').length;

      // Profils des utilisateurs connectés (optionnel — échec silencieux)
      const userIds = [...new Set(loginLogs.map(l => l.user_id).filter(Boolean))];
      let usersMap = {};
      if (userIds.length) {
        try {
          const { data: loginUsers } = await supabase
            .from('users').select('id, name, username, role, photo').in('id', userIds);
          (loginUsers || []).forEach(u => { usersMap[u.id] = u; });
        } catch { /* activité récente non critique */ }
      }

      const recentActivity = loginLogs.map(log => ({
        ...log,
        user_name:  usersMap[log.user_id]?.name || usersMap[log.user_id]?.username || log.new_value || '—',
        user_photo: usersMap[log.user_id]?.photo || null,
        user_role:  usersMap[log.user_id]?.role  || null,
      }));

      const alerts = [];
      if (criticalStock > 0)   alerts.push({ message: `${criticalStock} article(s) en rupture de stock`, severity: 'high' });
      if (pendingLeave  > 0)   alerts.push({ message: `${pendingLeave} demande(s) de congé en attente`, severity: 'medium' });
      if (delayedProjects > 0) alerts.push({ message: `${delayedProjects} projet(s) en retard`, severity: 'medium' });

      return { data: { stats: { totalPersonnel, totalVolunteers, activeProjects, delayedProjects, criticalStock, pendingLeave }, alerts, recentActivity } };
    } catch {
      // Retourner des zéros plutôt qu'une erreur bloquante
      return { data: empty };
    }
  }

  // ── PERSONNEL ─────────────────────────────────────────────
  if (segs[0] === 'personnel' && !segs[1]) {
    let q = supabase.from('personnel').select('*').order('last_name');
    if (params.get('search')) {
      const s = params.get('search');
      q = q.or(`first_name.ilike.%${s}%,last_name.ilike.%${s}%,matricule.ilike.%${s}%`);
    }
    if (params.get('service')) q = q.eq('service', params.get('service'));
    if (params.get('status'))  q = q.eq('status',  params.get('status'));
    const { data, error } = await q;
    if (error) throw err(error.message);
    return { data: { success: true, data: data || [] } };
  }
  if (segs[0] === 'personnel' && segs[1]) {
    const { data, error } = await supabase.from('personnel').select('*').eq('id', segs[1]).maybeSingle();
    if (error) throw err(error.message);
    if (!data) throw err('Personnel introuvable');
    return { data: { success: true, data } };
  }

  // ── ABSENCES ──────────────────────────────────────────────
  if (segs[0] === 'absences') {
    const { data, error } = await supabase
      .from('absences')
      .select('*, personnel(id, first_name, last_name)')
      .order('created_at', { ascending: false });
    if (error) throw err(error.message);
    // Aplatir : ajouter personnel_name
    const flat = (data || []).map(a => ({
      ...a,
      personnel_name: a.personnel ? `${a.personnel.first_name} ${a.personnel.last_name}` : '',
    }));
    return { data: { success: true, data: flat } };
  }

  // ── VOLONTAIRES ───────────────────────────────────────────
  if (segs[0] === 'volunteers' && !segs[1]) {
    let q = supabase.from('volontaires').select('*').order('last_name');
    if (params.get('search')) {
      const s = params.get('search');
      q = q.or(`first_name.ilike.%${s}%,last_name.ilike.%${s}%`);
    }
    if (params.get('status')) q = q.eq('status', params.get('status'));
    const { data, error } = await q;
    if (error) throw err(error.message);
    const mapped = (data || []).map(v => ({ ...v, join_date: v.assigned_date }));
    return { data: { success: true, data: mapped } };
  }
  if (segs[0] === 'volunteers' && segs[1]) {
    const { data, error } = await supabase.from('volontaires').select('*').eq('id', segs[1]).maybeSingle();
    if (error) throw err(error.message);
    if (!data) throw err('Volontaire introuvable');
    return { data: { success: true, data: { ...data, join_date: data.assigned_date } } };
  }

  // ── STOCK ─────────────────────────────────────────────────
  if (segs[0] === 'stock' && segs[1] === 'stats') {
    const { data } = await supabase.from('stock_articles').select('id, current_stock, min_stock, safety_stock, optimal_stock, unit_price, status');
    const items = (data || []).filter(i => i.status !== 'archived');
    const total      = items.length;
    const critical   = items.filter(i => (i.current_stock || 0) <= (i.min_stock || 0)).length;
    const safety     = items.filter(i => (i.current_stock || 0) > (i.min_stock || 0) && (i.current_stock || 0) <= (i.safety_stock || i.min_stock || 0)).length;
    const totalValue = items.reduce((s, i) => s + (i.current_stock || 0) * (i.unit_price || 0), 0);
    return { data: { total, critical, safety, totalValue } };
  }
  if (segs[0] === 'stock' && !segs[1]) {
    let q = supabase.from('stock_articles').select('*').order('name');
    if (params.get('search')) {
      const s = params.get('search');
      q = q.or(`name.ilike.%${s}%,reference.ilike.%${s}%`);
    }
    const { data, error } = await q;
    if (error) throw err(error.message);
    // Mapper vers les noms de champs attendus par le frontend
    const mapped = (data || []).map(i => ({
      ...i,
      code:          i.reference,
      quantity:      i.current_stock,
      category_id:   i.category,
      category_name: i.category,
      safety_stock:  i.safety_stock || i.min_stock || 0,
    }));
    return { data: { success: true, data: mapped } };
  }
  if (segs[0] === 'stock' && segs[1]) {
    const { data, error } = await supabase.from('stock_articles').select('*').eq('id', segs[1]).maybeSingle();
    if (error) throw err(error.message);
    if (!data) throw err('Article introuvable');
    return { data: { success: true, data: { ...data, code: data.reference, quantity: data.current_stock } } };
  }

  if (segs[0] === 'stock-categories') {
    const { data } = await supabase.from('stock_articles').select('category');
    const cats = [...new Set((data || []).map(i => i.category).filter(Boolean))];
    return { data: { success: true, data: cats.map(c => ({ id: c, name: c })) } };
  }

  if (segs[0] === 'stock-movements') {
    const { data, error } = await supabase
      .from('stock_mouvements')
      .select('id, type, quantity, reason, reference_doc, invoice_number, expiry_date, recipient, supplier, unit_price, created_at, article_id, stock_articles(name, reference, unit)')
      .order('created_at', { ascending: false })
      .limit(200);
    if (error) throw err(error.message);
    return { data: { success: true, data: data || [] } };
  }

  // ── PROJETS ───────────────────────────────────────────────
  if (segs[0] === 'projects' && segs[1] === 'stats') {
    const { data } = await supabase.from('projets').select('status, end_date');
    const items = data || [];
    return { data: {
      total:    items.length,
      active:   items.filter(p => p.status === 'active').length,
      delayed:  items.filter(p => p.end_date && p.end_date < today() && p.status !== 'completed').length,
      completed:items.filter(p => p.status === 'completed').length,
    }};
  }
  if (segs[0] === 'projects' && !segs[1]) {
    let q = supabase.from('projets').select('*').order('created_at', { ascending: false });
    if (params.get('search')) {
      const s = params.get('search');
      q = q.ilike('name', `%${s}%`);
    }
    if (params.get('status')) q = q.eq('status', params.get('status'));
    const { data, error } = await q;
    if (error) throw err(error.message);
    // Mapper budget_total → budget
    const mapped = (data || []).map(p => ({ ...p, budget: p.budget_total }));
    return { data: { success: true, data: mapped } };
  }
  if (segs[0] === 'projects' && segs[1]) {
    const { data, error } = await supabase.from('projets').select('*').eq('id', segs[1]).maybeSingle();
    if (error) throw err(error.message);
    if (!data) throw err('Projet introuvable');
    return { data: { success: true, data: { ...data, budget: data.budget_total } } };
  }

  // ── SERVICES ──────────────────────────────────────────────
  if (segs[0] === 'services') {
    const { data, error } = await supabase.from('services').select('*');
    if (error) throw err(error.message);
    return { data: { success: true, data: data || [] } };
  }

  // ── UTILISATEURS ─────────────────────────────────────────
  if (segs[0] === 'users' && !segs[1]) {
    const { data, error } = await supabase
      .from('users')
      .select('id, username, email, name, role, service, active, locked, last_login, photo')
      .order('name');
    if (error) throw err(error.message);
    return { data: { success: true, data: data || [] } };
  }

  // ── BCC ───────────────────────────────────────────────────
  if (segs[0] === 'bcc' && segs[1] === 'structure') {
    // Retourne la structure indexée par langue (même format que le backend)
    return { data: { success: true, data: COURSE_STRUCTURE } };
  }

  if (segs[0] === 'bcc' && segs[1] === 'stats') {
    const [{ data: students }, { data: lessons }] = await Promise.all([
      supabase.from('bcc_students').select('id, language, status'),
      supabase.from('bcc_lessons').select('student_id, class_number, lesson_number, score, max_score'),
    ]);

    const total = (students || []).length;
    const parLangue  = { Français: 0, English: 0, Malagasy: 0 };
    const parStatut  = { actif: 0, termine: 0, suspendu: 0 };
    const mentionMap = {};

    (students || []).forEach(s => {
      if      (s.language === 'Français' || s.language === 'fr') parLangue.Français++;
      else if (s.language === 'English'  || s.language === 'en') parLangue.English++;
      else if (s.language === 'Malagasy' || s.language === 'mg') parLangue.Malagasy++;

      if      (s.status === 'active')    parStatut.actif++;
      else if (s.status === 'completed') parStatut.termine++;
      else if (s.status === 'suspended') parStatut.suspendu++;

      const prog = calcProgression(s, lessons || []);
      if (prog.moyenneGlobale !== null) {
        const mention = getMentionBcc(prog.moyenneGlobale);
        if (mention) mentionMap[mention] = (mentionMap[mention] || 0) + 1;
      }
    });

    return { data: { success: true, data: { total, parLangue, parStatut, mentions: mentionMap } } };
  }

  if (segs[0] === 'bcc' && segs[1] === 'etudiants' && segs[2] === undefined) {
    const [{ data: students, error }, { data: lessons }] = await Promise.all([
      supabase.from('bcc_students').select('*').order('last_name'),
      supabase.from('bcc_lessons').select('*'),
    ]);
    if (error) throw err(error.message);
    let list = students || [];
    if (params.get('search')) {
      const s = params.get('search').toLowerCase();
      list = list.filter(st =>
        st.first_name.toLowerCase().includes(s) ||
        st.last_name.toLowerCase().includes(s) ||
        st.student_number.toLowerCase().includes(s)
      );
    }
    if (params.get('langue')) list = list.filter(st => st.language === params.get('langue'));
    if (params.get('statut')) list = list.filter(st => st.status  === params.get('statut'));

    return { data: { success: true, data: list.map(s => toEtudiant(s, lessons || [])) } };
  }

  if (segs[0] === 'bcc' && segs[1] === 'etudiants' && segs[2]) {
    const [{ data: student, error }, { data: lessons }] = await safeAll([
      supabase.from('bcc_students').select('*').eq('id', segs[2]).maybeSingle(),
      supabase.from('bcc_lessons').select('*').eq('student_id', segs[2]),
    ]);
    if (error) throw err(error.message);
    if (!student) throw err('Étudiant introuvable');
    return { data: { success: true, data: toEtudiant(student, lessons || []) } };
  }

  if (segs[0] === 'bcc' && segs[1] === 'etudiants' && segs[2] && segs[3] === 'notes') {
    const { data, error } = await supabase.from('bcc_lessons').select('*').eq('student_id', segs[2]);
    if (error) throw err(error.message);
    return { data: { success: true, data: data || [] } };
  }

  throw err(`Route introuvable : GET ${url}`);
}

// ═══════════════════════════════════════════════════════════════
// POST
// ═══════════════════════════════════════════════════════════════
async function post(url, body = {}) {
  const { segs } = parseUrl(url);

  // ── PERSONNEL ─────────────────────────────────────────────
  if (segs[0] === 'personnel') {
    const prefix = 'MAT';
    const { data: last } = await supabase.from('personnel').select('matricule').order('created_at', { ascending: false }).limit(1);
    const nextNum = last?.[0]?.matricule?.match(/\d+/)?.[0] ? parseInt(last[0].matricule.match(/\d+/)[0]) + 1 : 1;
    const matricule = `${prefix}-${String(nextNum).padStart(4, '0')}`;

    const { data, error } = await supabase.from('personnel').insert({
      id: uid(), matricule, ...pickPersonnel(body),
      emergency_contact: body.emergency_contact || null,
    }).select().single();
    if (error) throw err(error.message);
    return { data: { success: true, data } };
  }

  // ── ABSENCES ──────────────────────────────────────────────
  if (segs[0] === 'absences') {
    const { data, error } = await supabase.from('absences').insert(
      nullifyDates({ id: uid(), status: 'pending', created_at: now(), ...body })
    ).select().single();
    if (error) throw err(error.message);
    return { data: { success: true, data } };
  }

  // ── VOLONTAIRES ───────────────────────────────────────────
  if (segs[0] === 'volunteers') {
    const { join_date, ...rest } = body;
    const { data, error } = await supabase.from('volontaires').insert(
      nullifyDates({ id: uid(), status: 'enrolled', created_at: now(), ...rest, assigned_date: join_date || null })
    ).select().single();
    if (error) throw err(error.message);
    return { data: { success: true, data: data ? { ...data, join_date: data.assigned_date } : data } };
  }

  // ── STOCK ARTICLE ─────────────────────────────────────────
  if (segs[0] === 'stock') {
    const refNum = Date.now().toString().slice(-6);
    const { category_id, ...rest } = body;
    // Construire l'article avec toutes les colonnes avancées
    const articleData = {
      id: uid(),
      reference: `REF-${refNum}`,
      category: category_id || rest.category || '',
      current_stock: 0,
      created_at: now(),
      name:          rest.name          || '',
      description:   rest.description   || '',
      unit:          rest.unit          || 'pièce',
      unit_price:    parseInt(rest.unit_price)    || 0,
      min_stock:     parseInt(rest.min_stock)     || 0,
      safety_stock:  parseInt(rest.safety_stock)  || 0,
      optimal_stock: parseInt(rest.optimal_stock) || 0,
      supplier:      rest.supplier      || '',
      location:      rest.location      || '',
      subcategory:   rest.subcategory   || '',
      photo:         rest.photo         || '',
      specs:         rest.specs         || '',
      status:        'active',
    };
    let { data, error } = await supabase.from('stock_articles').insert(articleData).select().maybeSingle();
    // Fallback si colonnes avancées absentes du cache schema
    if (error && (error.message?.includes('schema cache') || error.message?.includes('column'))) {
      const { subcategory, optimal_stock, specs, photo, ...base } = articleData;
      const retry = await supabase.from('stock_articles').insert(base).select().maybeSingle();
      data = retry.data; error = retry.error;
    }
    if (error) throw err(error.message);
    return { data: { success: true, data: { ...data, code: data.reference, quantity: data.current_stock } } };
  }

  // ── STOCK MOUVEMENT ───────────────────────────────────────
  if (segs[0] === 'stock-movements') {
    const {
      type, item_id, article_id, quantity,
      reference_doc, reason,
      invoice_number, expiry_date, recipient, supplier, unit_price,
    } = body;
    const resolvedId = article_id || item_id;
    const qty = parseInt(quantity) || 0;
    // Normaliser le type : entry→entree, exit→sortie, transfer→transfert
    const dbType = type === 'entry' || type === 'entree' ? 'entree'
      : type === 'exit' || type === 'sortie'             ? 'sortie'
      : 'transfert';

    const { data: article } = await supabase.from('stock_articles').select('current_stock').eq('id', resolvedId).maybeSingle();
    if (!article) throw err('Article introuvable');
    if (dbType === 'sortie' && article.current_stock < qty) throw err(`Stock insuffisant (${article.current_stock} disponible)`);

    const newStock = dbType === 'entree' ? article.current_stock + qty : Math.max(0, article.current_stock - qty);

    // Construire le mouvement avec colonnes avancées
    const mvtData = {
      id: uid(), article_id: resolvedId, type: dbType, quantity: qty,
      reason: reason || '', reference_doc: reference_doc || '', created_at: now(),
      invoice_number: invoice_number || '',
      expiry_date:    expiry_date    || null,
      recipient:      recipient      || '',
      supplier:       supplier       || '',
      unit_price:     parseInt(unit_price) || 0,
    };
    let { error: mvtError } = await supabase.from('stock_mouvements').insert(mvtData);
    // Fallback si colonnes avancées absentes
    if (mvtError && (mvtError.message?.includes('schema cache') || mvtError.message?.includes('column'))) {
      const baseMvt = { id: mvtData.id, article_id: resolvedId, type: dbType, quantity: qty, reason: reason||'', reference_doc: reference_doc||'', created_at: now() };
      ({ error: mvtError } = await supabase.from('stock_mouvements').insert(baseMvt));
    }
    const { error: updError } = await supabase.from('stock_articles').update({ current_stock: newStock }).eq('id', resolvedId);
    if (mvtError || updError) throw err((mvtError || updError).message);
    return { data: { success: true } };
  }

  // ── PROJETS ───────────────────────────────────────────────
  if (segs[0] === 'projects') {
    // Séparer les colonnes avancées (potentiellement absentes du cache schema) des colonnes de base
    const { budget, coordinator, phases, objectives, team_members, risks, ...rest } = body;
    const { data, error } = await supabase.from('projets').insert(
      nullifyDates({ id: uid(), budget_total: budget || 0, created_at: now(), ...rest })
    ).select().single();
    if (error) throw err(error.message);
    return { data: { success: true, data: { ...data, budget: data.budget_total } } };
  }

  // ── UTILISATEURS ─────────────────────────────────────────
  // Passe par le backend pour la création Supabase Auth sans confirmation email
  if (segs[0] === 'users') {
    return callBackend('POST', '/users', body);
  }

  // ── BCC ÉTUDIANT ──────────────────────────────────────────
  if (segs[0] === 'bcc' && segs[1] === 'etudiants') {
    const { data: last } = await supabase.from('bcc_students').select('student_number').order('created_at', { ascending: false }).limit(1);
    const lastNum = last?.[0]?.student_number?.match(/\d+/)?.[0] ? parseInt(last[0].student_number.match(/\d+/)[0]) : 0;
    const student_number = `BCC-${String(lastNum + 1).padStart(4, '0')}`;

    const { data, error } = await supabase.from('bcc_students').insert({
      id: uid(), student_number,
      first_name: body.prenom || body.first_name,
      last_name:  body.nom    || body.last_name,
      language:   body.langue || body.language || 'Français',
      status: body.statut || body.status || 'active',
      registration_date: today(),
      created_at: now(),
      birth_date:      body.birth_date      || null,
      birth_place:     body.birth_place     || null,
      address:         body.address         || null,
      phone:           body.phone           || null,
      email:           body.email           || null,
      family_situation:body.family_situation|| null,
      education_level: body.education_level || null,
      occupation:      body.occupation      || null,
      motivation:      body.motivation      || null,
    }).select().single();
    if (error) throw err(error.message);
    return { data: { success: true, data: toEtudiant(data, []) } };
  }

  // ── BCC NOTE ──────────────────────────────────────────────
  if (segs[0] === 'bcc' && segs[1] === 'notes') {
    const { etudiant_id, annee_id, lecon, note } = body;

    // Récupérer la langue de l'étudiant pour utiliser la bonne structure
    const { data: studentRow } = await supabase.from('bcc_students').select('language').eq('id', etudiant_id).maybeSingle();
    const struct  = COURSE_STRUCTURE[studentRow?.language] || COURSE_STRUCTURE['Français'];
    const annee   = struct.annees.find(a => a.id === annee_id);
    const lesson  = annee?.lessons.find(l => l.num === lecon);
    if (lesson && parseFloat(note) > lesson.max) throw err(`Note dépasse le maximum autorisé (${lesson.max})`);

    // Vérifier si la note existe déjà
    const { data: existing } = await supabase.from('bcc_lessons')
      .select('id')
      .eq('student_id', etudiant_id)
      .eq('class_number', annee_id)
      .eq('lesson_number', lecon)
      .maybeSingle();

    let data, error;
    if (existing?.id) {
      // Mise à jour
      ({ data, error } = await supabase.from('bcc_lessons')
        .update({ score: parseFloat(note), max_score: lesson?.max ?? 0, date_taken: today() })
        .eq('id', existing.id)
        .select().single());
    } else {
      // Insertion
      ({ data, error } = await supabase.from('bcc_lessons')
        .insert({
          id: uid(), student_id: etudiant_id, class_number: annee_id,
          lesson_number: lecon, score: parseFloat(note),
          max_score: lesson?.max ?? 0, date_taken: today(), created_at: now(),
        }).select().single());
    }
    if (error) throw err(error.message);
    return { data: { success: true, data } };
  }

  // ── IA CHAT ───────────────────────────────────────────────
  if (segs[0] === 'ai' && segs[1] === 'chat') {
    return callAI('chat', body);
  }

  // ── IA PREDICT ────────────────────────────────────────────
  if (segs[0] === 'ai' && segs[1] === 'predict') {
    return callAI('predict', body);
  }

  throw err(`Route introuvable : POST ${url}`);
}

// ═══════════════════════════════════════════════════════════════
// PUT
// ═══════════════════════════════════════════════════════════════
async function put(url, body = {}) {
  const { segs } = parseUrl(url);

  // ── PROFIL UTILISATEUR COURANT ────────────────────────────
  if (segs[0] === 'users' && segs[1] === 'me' && segs[2] === 'profile') {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw err('Non authentifié');

    if (body.newPassword) {
      // ── Changement de mot de passe ──────────────────────
      // 1. Vérifier l'ancien mot de passe via re-authentification
      if (!body.oldPassword) throw err('Ancien mot de passe requis');
      const { error: reAuthErr } = await supabase.auth.signInWithPassword({
        email: session.user.email, password: body.oldPassword,
      });
      if (reAuthErr) throw err('Ancien mot de passe incorrect');

      // 2. Mettre à jour dans Supabase Auth
      const { error: pwdErr } = await supabase.auth.updateUser({ password: body.newPassword });
      if (pwdErr) throw err(pwdErr.message);

      return { data: { success: true } };
    } else {
      // ── Mise à jour du profil (nom, email, photo) ───────
      const { name, email, photo } = body;
      const updates = {};
      if (name  !== undefined) updates.name  = name;
      if (photo !== undefined) updates.photo = photo;

      // Si email change : vérification unicité + sync Supabase Auth
      if (email !== undefined && email !== session.user.email) {
        const { data: existing } = await supabase.from('users')
          .select('id').eq('email', email).maybeSingle();
        if (existing) throw err('Email déjà utilisé par un autre utilisateur');

        updates.email = email;

        // Synchroniser Supabase Auth (email login)
        const { error: authEmailErr } = await supabase.auth.updateUser({ email });
        if (authEmailErr) console.warn('Sync email Supabase Auth:', authEmailErr.message);
      }

      // Requête par email courant (identifiant stable jusqu'à confirmation)
      const { data, error } = await supabase.from('users')
        .update(updates)
        .eq('email', session.user.email)
        .select('id, username, email, name, role, service, active, locked, photo')
        .maybeSingle();
      if (error) throw err(error.message);
      if (!data) throw err('Utilisateur introuvable');
      return { data: { success: true, data } };
    }
  }

  // ── UTILISATEURS (admin) — passe par le backend ──────────
  if (segs[0] === 'users' && segs[2] === 'toggle-block') {
    return callBackend('PUT', `/users/${segs[1]}/toggle-block`);
  }
  if (segs[0] === 'users' && segs[1]) {
    // Le backend gère le hash du password + sync Supabase Auth
    return callBackend('PUT', `/users/${segs[1]}`, body);
  }

  // ── PERSONNEL ─────────────────────────────────────────────
  if (segs[0] === 'personnel' && segs[1]) {
    const { data, error } = await supabase.from('personnel').update(pickPersonnel(body)).eq('id', segs[1]).select().single();
    if (error) throw err(error.message);
    return { data: { success: true, data } };
  }

  // ── ABSENCES ──────────────────────────────────────────────
  if (segs[0] === 'absences' && segs[1]) {
    const { status, ...rest } = body;
    const updates = { ...rest };
    if (status) {
      updates.status = status;
      if (status === 'approved' || status === 'rejected') updates.approved_at = now();
    }
    const { data, error } = await supabase.from('absences').update(updates).eq('id', segs[1]).select().single();
    if (error) throw err(error.message);
    return { data: { success: true, data } };
  }

  // ── VOLONTAIRES ───────────────────────────────────────────
  if (segs[0] === 'volunteers' && segs[2] === 'workflow') {
    const WORKFLOW = ['enrolled', 'evaluated', 'assigned', 'active', 'recognized'];
    const { data: v } = await supabase.from('volontaires').select('status').eq('id', segs[1]).maybeSingle();
    const idx  = WORKFLOW.indexOf(v?.status);
    const next = idx >= 0 && idx < WORKFLOW.length - 1 ? WORKFLOW[idx + 1] : v?.status;
    const { data, error } = await supabase.from('volontaires').update({ status: next }).eq('id', segs[1]).select().single();
    if (error) throw err(error.message);
    return { data: { success: true, data } };
  }
  if (segs[0] === 'volunteers' && segs[1]) {
    const { join_date, ...rest } = body;
    const updates = { ...rest };
    if (join_date !== undefined) updates.assigned_date = join_date || null;
    const { data, error } = await supabase.from('volontaires').update(updates).eq('id', segs[1]).select().single();
    if (error) throw err(error.message);
    return { data: { success: true, data: data ? { ...data, join_date: data.assigned_date } : data } };
  }

  // ── STOCK ─────────────────────────────────────────────────
  if (segs[0] === 'stock' && segs[1]) {
    const { category_id, quantity, code, ...rest } = body;
    const updates = { ...rest };
    if (category_id !== undefined) updates.category = category_id;
    if (updates.unit_price    !== undefined) updates.unit_price    = parseInt(updates.unit_price)    || 0;
    if (updates.min_stock     !== undefined) updates.min_stock     = parseInt(updates.min_stock)     || 0;
    if (updates.safety_stock  !== undefined) updates.safety_stock  = parseInt(updates.safety_stock)  || 0;
    if (updates.optimal_stock !== undefined) updates.optimal_stock = parseInt(updates.optimal_stock) || 0;
    let { data, error } = await supabase.from('stock_articles').update(updates).eq('id', segs[1]).select().single();
    // Fallback si colonnes avancées absentes du cache schema
    if (error && (error.message?.includes('schema cache') || error.message?.includes('column'))) {
      const { subcategory, optimal_stock, specs, photo, ...baseUpdates } = updates;
      const retry = await supabase.from('stock_articles').update(baseUpdates).eq('id', segs[1]).select().single();
      data = retry.data; error = retry.error;
    }
    if (error) throw err(error.message);
    return { data: { success: true, data: { ...data, code: data.reference, quantity: data.current_stock } } };
  }

  // ── PROJETS ───────────────────────────────────────────────
  if (segs[0] === 'projects' && segs[1]) {
    const { budget, coordinator, phases, objectives, team_members, risks, ...baseRest } = body;
    const baseUpdates = { ...baseRest };
    if (budget !== undefined) baseUpdates.budget_total = budget;

    // Construire la mise à jour complète (avec colonnes avancées si elles existent)
    const fullUpdates = { ...baseUpdates };
    if (phases       !== undefined) fullUpdates.phases       = phases;
    if (objectives   !== undefined) fullUpdates.objectives   = objectives;
    if (team_members !== undefined) fullUpdates.team_members = team_members;
    if (risks        !== undefined) fullUpdates.risks        = risks;

    let { data, error } = await supabase.from('projets').update(fullUpdates).eq('id', segs[1]).select().single();

    // Si Supabase ne reconnaît pas une colonne avancée (schema cache), réessayer sans elles
    if (error && (error.message?.includes('schema cache') || error.message?.includes('column'))) {
      const retry = await supabase.from('projets').update(baseUpdates).eq('id', segs[1]).select().single();
      data  = retry.data;
      error = retry.error;
    }

    if (error) throw err(error.message);
    return { data: { success: true, data: { ...data, budget: data.budget_total } } };
  }

  // ── BCC ÉTUDIANT ──────────────────────────────────────────
  if (segs[0] === 'bcc' && segs[1] === 'etudiants' && segs[2]) {
    const updates = {
      first_name: body.prenom || body.first_name,
      last_name:  body.nom    || body.last_name,
      language:   body.langue || body.language,
      status:     body.statut || body.status,
    };
    Object.keys(updates).forEach(k => updates[k] === undefined && delete updates[k]);
    const { data, error } = await supabase.from('bcc_students').update(updates).eq('id', segs[2]).select().single();
    if (error) throw err(error.message);
    const { data: lessons } = await supabase.from('bcc_lessons').select('*').eq('student_id', segs[2]);
    return { data: { success: true, data: toEtudiant(data, lessons || []) } };
  }

  throw err(`Route introuvable : PUT ${url}`);
}

// ═══════════════════════════════════════════════════════════════
// DELETE
// ═══════════════════════════════════════════════════════════════
async function del(url) {
  const { segs } = parseUrl(url);

  if (segs[0] === 'personnel' && segs[1]) {
    // Supprimer les absences liées d'abord (contrainte FK)
    await supabase.from('absences').delete().eq('personnel_id', segs[1]);
    const { error } = await supabase.from('personnel').delete().eq('id', segs[1]);
    if (error) throw err(error.message);
    return { data: { success: true } };
  }

  if (segs[0] === 'absences' && segs[1]) {
    const { error } = await supabase.from('absences').delete().eq('id', segs[1]);
    if (error) throw err(error.message);
    return { data: { success: true } };
  }

  if (segs[0] === 'stock' && segs[1]) {
    const { error } = await supabase.from('stock_articles').delete().eq('id', segs[1]);
    if (error) throw err(error.message);
    return { data: { success: true } };
  }

  if (segs[0] === 'bcc' && segs[1] === 'etudiants' && segs[2]) {
    // Supprimer les notes d'abord
    await supabase.from('bcc_lessons').delete().eq('student_id', segs[2]);
    const { error } = await supabase.from('bcc_students').delete().eq('id', segs[2]);
    if (error) throw err(error.message);
    return { data: { success: true } };
  }

  if (segs[0] === 'bcc' && segs[1] === 'notes' && segs[2]) {
    const { error } = await supabase.from('bcc_lessons').delete().eq('id', segs[2]);
    if (error) throw err(error.message);
    return { data: { success: true } };
  }

  if (segs[0] === 'volunteers' && segs[1]) {
    const { error } = await supabase.from('volontaires').delete().eq('id', segs[1]);
    if (error) throw err(error.message);
    return { data: { success: true } };
  }

  if (segs[0] === 'projects' && segs[1]) {
    const { error } = await supabase.from('projets').delete().eq('id', segs[1]);
    if (error) throw err(error.message);
    return { data: { success: true } };
  }

  // ── UTILISATEURS (admin) — passe par le backend ──────────
  if (segs[0] === 'users' && segs[1]) {
    return callBackend('DELETE', `/users/${segs[1]}`);
  }

  throw err(`Route introuvable : DELETE ${url}`);
}

// ═══════════════════════════════════════════════════════════════
// IA — Supabase Edge Function proxy
// ═══════════════════════════════════════════════════════════════
async function callAI(type, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const EDGE_URL = process.env.REACT_APP_AI_EDGE_URL;
  if (!EDGE_URL) {
    throw err('Fonction Edge IA non configurée. Ajoutez REACT_APP_AI_EDGE_URL dans votre .env.', 'NO_API_KEY');
  }

  const res = await fetch(`${EDGE_URL}/${type}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw err(e.message || 'Erreur IA', e.code || 'AI_ERROR');
  }
  const json = await res.json();
  return { data: json };
}

// ─────────────────────────────────────────────────────────────
const api = { get, post, put, delete: del };
export default api;
